import { useState, useEffect, useCallback } from 'react';
import api from '@/api/client';
import type { DashboardOverview, TrendData, DistributionData, RealtimeData } from '@/types';

interface DashboardState {
  overview: DashboardOverview | null;
  trends: TrendData | null;
  distribution: DistributionData | null;
  realtime: RealtimeData | null;
}

export function useDashboard() {
  const [data, setData] = useState<DashboardState>({
    overview: null,
    trends: null,
    distribution: null,
    realtime: null,
  });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, distributionRes, realtimeRes] = await Promise.all([
        api.get('/dashboard/overview'),
        api.get('/dashboard/trends', { params: { days: 30 } }),
        api.get('/dashboard/distribution'),
        api.get('/dashboard/realtime'),
      ]);
      setData({
        overview: overviewRes.data,
        trends: trendsRes.data,
        distribution: distributionRes.data,
        realtime: realtimeRes.data,
      });
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { ...data, loading, refresh: loadData };
}
